// Phase B6 — Marriage/Police → Apostille cross-link card.
//
// Why: Apostille is the canonical post-marriage / post-police-clearance step
// for any document a Thai applicant takes abroad. Surfacing the per-destination
// Apostille deep page from Marriage + Police country pages funnels PageRank
// into the highest-ARPU money page in the cluster, and gives Google / SGE /
// Perplexity a clean topical edge: "marriage with German national" ↔
// "apostille Thai documents for Germany".
//
// No new routes. Pure forward-link. Renders nothing if the target apostille
// country page doesn't exist, so it never points at a 404.

import { Link } from "@tanstack/react-router";
import { ArrowRight, Stamp, ShieldCheck, FileCheck2 } from "lucide-react";
import { APOSTILLE_COUNTRIES } from "@/data/apostille-countries";
import { APOSTILLE_DEEP_EN } from "@/data/apostille-deep-en";

// Mapping from Police-target-country slug (or any caller slug) to the
// Apostille country slug. Only entries that differ are listed; identical
// slugs (germany, france, japan, ...) are resolved automatically.
const POLICE_TO_APOSTILLE: Record<string, string> = {
  "united-kingdom": "uk",
  korea: "south-korea",
};

// Mapping from Marriage nationality slug to Apostille country slug.
const NATIONALITY_TO_APOSTILLE: Record<string, string> = {
  american: "usa",
  canadian: "canada",
  british: "uk",
  german: "germany",
  french: "france",
  italian: "italy",
  spanish: "spain",
  dutch: "netherlands",
  belgian: "belgium",
  swiss: "switzerland",
  swedish: "sweden",
  norwegian: "norway",
  danish: "denmark",
  finnish: "finland",
  austrian: "austria",
  portuguese: "portugal",
  irish: "ireland",
  japanese: "japan",
  korean: "south-korea",
  chinese: "china",
  indian: "india",
  filipino: "philippines",
  singaporean: "singapore",
  australian: "australia",
  emirati: "uae",
  russian: "russia",
};

export function resolveApostilleSlug(
  source: "police" | "nationality",
  slug: string,
): string | null {
  const map = source === "police" ? POLICE_TO_APOSTILLE : NATIONALITY_TO_APOSTILLE;
  const candidate = map[slug] ?? slug;
  return APOSTILLE_COUNTRIES.some((c) => c.slug === candidate) ? candidate : null;
}

interface Props {
  mode: "th" | "en";
  /** Origin context — drives mapping table + headline copy. */
  source: "police" | "nationality";
  /** Source slug (police target country slug OR marriage nationality slug). */
  sourceSlug: string;
  /** Localised display name of the destination country / spouse home country. */
  countryLabel: string;
}

export default function ApostilleCrossLinks({ mode, source, sourceSlug, countryLabel }: Props) {
  const apoSlug = resolveApostilleSlug(source, sourceSlug);
  if (!apoSlug) return null;

  const country = APOSTILLE_COUNTRIES.find((c) => c.slug === apoSlug)!;
  const isTh = mode === "th";
  const hasDeep = Boolean(APOSTILLE_DEEP_EN[apoSlug]);
  const isHague = country.isHague;

  const sourceContextTh =
    source === "police"
      ? `หลังได้ใบรับรองความประพฤติ (PCSC) แล้ว เอกสารส่วนใหญ่ที่ใช้ยื่นวีซ่า${countryLabel}ยังต้องผ่านขั้นตอน ${isHague ? "Apostille" : "Legalization"} ของกระทรวงต่างประเทศก่อนจึงจะใช้ได้ที่ปลายทาง`
      : `หลังจดทะเบียนสมรสในไทย คุณยังต้องนำใบ คร.2 / คร.3 ผ่าน ${isHague ? "Apostille" : "Legalization + สถานทูต"} เพื่อให้${countryLabel}รับรองสถานะสมรสและออกวีซ่าคู่สมรส`;

  const sourceContextEn =
    source === "police"
      ? `Once your PCSC is in hand, most supporting documents for a ${countryLabel} visa still need MFA ${isHague ? "Apostille" : "legalization + embassy attestation"} before the receiving authority will accept them.`
      : `After your Thai marriage registration, the Kor.Ror.2/3 certificate must still pass ${isHague ? "MFA Apostille" : "MFA legalization + embassy attestation"} before ${countryLabel} will recognise your marital status for a spouse visa.`;

  const ribbonTh = isHague
    ? "Hague Apostille (Thailand เข้าร่วม 14 ธ.ค. 2024)"
    : "Consular Legalization (ไม่ใช่สมาชิก Hague)";
  const ribbonEn = isHague
    ? "Hague Apostille (Thailand acceded 14 Dec 2024)"
    : "Consular Legalization (non-Hague pathway)";

  return (
    <section className="mt-14 border-t border-slate-200 pt-10">
      <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">
        {isTh ? `ขั้นตอนถัดไป — ${ribbonTh}` : `Next step — ${ribbonEn}`}
      </div>
      <h2 className="mt-1 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
        {isTh
          ? `${isHague ? "Apostille" : "Legalization"} เอกสารไทย เพื่อใช้ที่${countryLabel} ${country.flag}`
          : `${isHague ? "Apostille" : "Legalize"} Thai documents for use in ${countryLabel} ${country.flag}`}
      </h2>
      <p className="mt-3 max-w-3xl text-[14.5px] leading-relaxed text-slate-700">
        {isTh ? sourceContextTh : sourceContextEn}
      </p>

      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        <Link
          to={isTh ? "/apostille/$country" : "/en/apostille/$country"}
          params={{ country: apoSlug }}
          className="group flex items-start gap-3 rounded-2xl border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-white p-5 transition hover:-translate-y-0.5 hover:border-gold hover:shadow-md"
          data-track-zone="apostille-cross-link"
        >
          <Stamp className="mt-0.5 h-6 w-6 shrink-0 text-amber-600" />
          <div className="flex-1">
            <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700">
              {hasDeep
                ? (isTh ? "คู่มือเชิงลึก ฉบับสมบูรณ์" : "Comprehensive deep guide")
                : (isTh ? "หน้าหลักประเทศ" : "Destination hub")}
            </div>
            <div className="mt-0.5 font-display text-base font-extrabold text-navy-deep group-hover:text-amber-700">
              {isTh
                ? `${isHague ? "Apostille" : "Legalization"} สำหรับ${countryLabel}`
                : `${isHague ? "Apostille" : "Legalization"} for ${countryLabel}`}
            </div>
            <div className="mt-1 text-[12px] text-slate-600">
              {isTh
                ? `รับรองโดย ${country.authority} · เริ่ม ฿${country.priceFromTh.toLocaleString()} · ${country.turnaround}`
                : `Accepted by ${country.authority} · From THB ${country.priceFromTh.toLocaleString()} · ${country.turnaround}`}
            </div>
            <div className="mt-3 inline-flex items-center gap-1 text-[12px] font-bold text-amber-700 group-hover:gap-1.5">
              {isTh ? "อ่านขั้นตอน + ราคา" : "View pipeline + pricing"}
              <ArrowRight className="h-3.5 w-3.5" />
            </div>
          </div>
        </Link>

        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-slate-500">
            <ShieldCheck className="h-4 w-4 text-emerald-600" />
            {isTh ? "ทำไมต้องผ่าน NYC" : "Why route through NYC"}
          </div>
          <ul className="mt-3 space-y-2 text-[13px] text-slate-700">
            <li className="flex items-start gap-2">
              <FileCheck2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              {isTh
                ? "นักแปลรับรอง MoJ + Notary ในไฟล์เดียว ลดรอบส่งกลับ"
                : "MoJ-listed sworn translation bundled with MFA submission — no round-trips."}
            </li>
            <li className="flex items-start gap-2">
              <FileCheck2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              {isTh
                ? `เช็คฟอร์แมตให้ตรงกับ ${country.authority} ก่อนยื่นจริง`
                : `Pre-flight format check against ${country.authority} acceptance rules.`}
            </li>
            <li className="flex items-start gap-2">
              <FileCheck2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              {isTh
                ? "ติดตามสถานะ e-Authentication ของกรมการกงสุลให้ทุกขั้น"
                : "Tracked end-to-end on the MFA Department of Consular Affairs e-Authentication portal."}
            </li>
          </ul>
        </div>
      </div>
    </section>
  );
}
